import { h, Component } from 'preact'
import qs from 'qs'
import axios, {AxiosResponse} from 'axios'
import globals from '../../utils/globals'
import Header from '../header'
import Loader from '../ui-components/Loader'
import OverviewFilters from './OverviewFilters'
import PaginationComponent from '../ui-components/PaginationComponent'
import Table from '../table/Table'
import EmptyPage from '../empty-page/EmptyPage'

export type Filter = {
  id: string,
  name: string,
  type: string
}

export type Filters = {
  [filter: string]: Array<Filter>
}

export type FilterSelector = {
  key: string,
  data: [],
  displayName: string,
  component: Function
}

type Props = {
  type: string,
  withFilters?: Array<FilterSelector>,
  withSearch?: boolean,
  withSorting?: {
    order: string,
    direction: string
  },
  withPagination?: {
    page: number,
    pageSize: number
  },
  counterLabel: string,
  rowId: string,
  endpoint: string,
  dataKey: string,
  model: {},
  EmptyStateComponent: JSX.Element
}

type State = {
  data: [],
  total?: string,
  loading: boolean,
  areFiltersApplied: boolean,
  filters?: Filters,
  pagination?: {
    page: number,
    pageSize: number
  },
  sort?: {
    direction: string,
    order: string,
  },
  [key: string]: any
}

class Overview extends Component<Props, State> {
  private STORAGE_KEY = `${this.props.type}-filter-criteria`
  private defaultCdn = globals('defaultCdn')

  constructor (props: Props) {
    super(props)
    this.state = {
      data: [],
      total: undefined,
      loading: true,
      areFiltersApplied: false,
      ...this.getDefaultFilteringOptions()
    }

    this.handleResponse = this.handleResponse.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onClearFilterCriteria = this.onClearFilterCriteria.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
  }

  getDefaultFilteringOptions () : { [field: string] : {}} {
    const { withFilters, withSearch, withSorting, withPagination } = this.props
    const filteringOptions = {}

    if (withFilters) {
      const filterSelectors = withFilters
      const emptyFilters = {}
      filterSelectors.map((selector: FilterSelector) => {
        Object.assign(emptyFilters, { [selector.key]: [] })
      })

      if (withSearch) {
        Object.assign(emptyFilters, { search: [] })
      }

      Object.assign(filteringOptions, { filters: emptyFilters })
    }

    if (withSorting) {
      const { direction, order } = withSorting
      Object.assign(filteringOptions, { sort: { direction, order } })
    }

    if (withPagination) {
      const { page, pageSize } = withPagination
      Object.assign(filteringOptions, { pagination: { page, pageSize } })
    }

    return filteringOptions
  }

  async componentDidMount () {
    if (!window.sessionStorage.getItem(this.STORAGE_KEY)) {
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
    } else {
      const filterCriteria = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      const areFiltersApplied = JSON.stringify(filterCriteria.filters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
      this.setState({ ...filterCriteria, ...{ areFiltersApplied } })
    }

    await this.fetch()
  }

  async fetch () {
    this.setState({ loading: true })
    const url = this.getRequestUrl()
    await axios.get(url)
      .then(res => this.handleResponse(res))
      .catch(() => this.setState({ loading: false }))
  }

  getRequestUrl () {
    const { filters, pagination, sort } = this.state
    const { endpoint } = this.props
    const query = {}

    if (pagination) Object.assign(query, { ...pagination })

    if (filters) {
      const y = {}
      Object.keys(filters).forEach((key: any) => {
        const normalizedFilters: Array<Filter> = filters[key]
        if (normalizedFilters.length === 0) return
        const ids = normalizedFilters.map((filter: Filter) => filter.id)
        Object.assign(y, { [key]: ids})
      })
      Object.assign(query, { ...y })
    }

    if (sort) Object.assign(query, {...sort })

    return `${endpoint}${qs.stringify(query, { indices: false, addQueryPrefix: true, encode: false })}`
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination?.pageSize
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onPageSizeChange (pageSize: number) {
    const page = this.props.withPagination?.page
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onSortChange () {
    const direction = this.state.sort?.direction
    const order = this.state.sort?.order

    if (direction === 'desc') this.updateFilterCriteria('sort', { direction: 'asc', order })
    if (direction === 'asc') this.updateFilterCriteria('sort', { direction: 'desc', order })
  }

  onFilterChange (filter: string, payload: Array<Filter>) {
    const { filters } = this.state
    this.updateFilterCriteria('filters', {...filters, ...{ [filter]: payload }})
    // always to to the first page when updating filters
    const pagination = { page: 1, pageSize: this.state.pagination!.pageSize }
    this.setState({ areFiltersApplied: true, pagination })
  }

  onClearFilterCriteria () {
    this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
      this.fetch().then(() => {
        window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
      })
    })
  }

  updateFilterCriteria (key: string, value: {}) {
    let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
    storage[key] = value
    window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
    this.setState({ [key]: storage[key] }, () => {
      this.fetch()
    })
  }

  handleResponse (response: AxiosResponse) {
    const { total } = response.data
    const data = response.data[this.props.dataKey]

    this.setState({
      data: data || [],
      total: total || undefined,
      loading: false
    })
  }

  getPaginationProps () {
    const { withPagination } = this.props
    if(!withPagination) return

    const { pagination, total } = this.state

    return {
      pagination: Object.assign({},  pagination, { total }),
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
  }

  getTableProps () {
    const { model, rowId } = this.props
    const { data } = this.state

    const tableProps = { model, rows: data, rowId}

    if (this.props.withSorting) {
      const { sort } = this.state
      Object.assign(tableProps, { sort, onSortChange: this.onSortChange })
    }

    return tableProps
  }

  render ({
    withSearch,
    withPagination,
    withFilters,
    counterLabel,
    rowId,
    model,
    EmptyStateComponent
  }: Props, {
    data,
    total,
    filters,
    loading,
    areFiltersApplied,
    sort
  }: State) {

    return <div class='overview'>
      {loading && <Loader />}
      <Header title='Overview' disableCancelButton disableSaveButton />
      { withFilters && (data.length > 0 || areFiltersApplied) && <OverviewFilters
        filterSelectors={withFilters}
        filters={filters}
        onFilterChange={this.onFilterChange}
        onClearFilterCriteria={this.onClearFilterCriteria}
        withSearch={withSearch}
      /> }
      {data.length > 0
        ? <div>
          { withPagination && <div class='overview__table-info'>
            <span class='overview__counter'>{counterLabel} ({total})</span>
            <PaginationComponent {...this.getPaginationProps()} />
          </div> }
          <Table {...this.getTableProps()} />
          { withPagination && <PaginationComponent {...this.getPaginationProps()} displayListUpwards /> }
        </div>
        : (!loading && areFiltersApplied)
          ? <div>
            <EmptyPage
              img_src={`${this.defaultCdn}/images/empty_result.png`}
              header='No results found'
              text={'Sorry, that filter combination has no results.\n Please try different criteria.'}
            />
          </div> : !loading && EmptyStateComponent
      }
    </div>
  }
}

export default Overview
